import { config } from "../../config";
import { normalizePointer } from "./pointer";
import type { ActionName, InputSample, InputSource } from "../../types/input";

/**
 * Codes physiques et non caractères : `KeyW` est la touche en haut à gauche
 * du bloc, soit W en QWERTY et Z en AZERTY. Le ZQSD marche sans code dédié.
 */
const MOVE_KEYS: Readonly<Record<string, { x: number; y: number }>> = {
  KeyW: { x: 0, y: 1 },
  ArrowUp: { x: 0, y: 1 },
  KeyS: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: -1 },
  KeyA: { x: -1, y: 0 },
  ArrowLeft: { x: -1, y: 0 },
  KeyD: { x: 1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
};

/** Boutons de souris, selon la convention des événements pointeur. */
const LEFT_BUTTON = 0;
const MIDDLE_BUTTON = 1;
const RIGHT_BUTTON = 2;

/**
 * Actions au clavier uniquement : le clic gauche désigne, le droit et la
 * molette font tourner la caméra ([TD-044]). Le mapping définitif dépend du
 * gameplay (open-questions.md).
 */
const ACTION_KEYS: Readonly<Record<string, ActionName>> = {
  Space: "jump",
  KeyE: "primary",
  KeyF: "secondary",
};

export class KeyboardMouse implements InputSource {
  readonly name = "keyboard-mouse";

  private readonly pressedKeys = new Set<string>();
  private readonly lookDelta = { x: 0, y: 0 };
  /** Pixels de molette en attente d'être restitués, cf. config.input.wheelReleaseTime. */
  private zoomReservoir = 0;
  /** Orbite en cours, au clic droit ou molette uniquement. */
  private orbiting = false;
  /** Bouton gauche enfoncé : lui seul désigne, il ne tourne jamais. */
  private pressingLeft = false;
  /** Position normalisée du curseur, conservée entre deux mouvements. */
  private readonly pointer = { x: 0, y: 0 };
  private pointerPresent = false;
  private pick = false;
  /** Position normalisée au moment de l'enfoncement, celle que le joueur visait. */
  private readonly pressPointer = { x: 0, y: 0 };
  private readonly listeners = new AbortController();

  constructor(private readonly target: HTMLElement) {
    const { signal } = this.listeners;

    window.addEventListener("keydown", this.onKeyDown, { signal });
    window.addEventListener("keyup", this.onKeyUp, { signal });
    // Sans ça, un alt-tab pendant un appui laisse la touche « enfoncée ».
    window.addEventListener("blur", this.onBlur, { signal });

    target.addEventListener("pointerdown", this.onPointerDown, { signal });
    target.addEventListener("pointermove", this.onPointerMove, { signal });
    target.addEventListener("pointerup", this.onPointerUp, { signal });
    target.addEventListener("pointercancel", this.onPointerUp, { signal });
    target.addEventListener("wheel", this.onWheel, { signal, passive: false });
    target.addEventListener("contextmenu", this.onContextMenu, { signal });
    target.addEventListener("pointerleave", this.onPointerLeave, { signal });
  }

  drain(out: InputSample, dt: number): void {
    for (const code of this.pressedKeys) {
      const move = MOVE_KEYS[code];
      if (move) {
        out.move.x += move.x;
        out.move.y += move.y;
      }
      const action = ACTION_KEYS[code];
      if (action) out.actions[action] = true;
    }

    // Sur l'image d'une désignation, c'est la position visée qui est publiée ;
    // le survol la rejoint pour une image, puis suit de nouveau le curseur.
    const source = this.pick ? this.pressPointer : this.pointer;
    out.pointer.x = source.x;
    out.pointer.y = source.y;
    out.pointerPresent ||= this.pointerPresent;
    // Le curseur est le point de convergence du zoom quand il survole ([GD-017]).
    out.zoomAnchored ||= this.pointerPresent;
    out.pick ||= this.pick;
    this.pick = false;

    out.lookDelta.x += this.lookDelta.x;
    out.lookDelta.y += this.lookDelta.y;
    this.lookDelta.x = 0;
    this.lookDelta.y = 0;

    // Décroissance exponentielle : le total restitué reste égal au delta reçu,
    // seule sa répartition dans le temps change.
    const released =
      this.zoomReservoir * (1 - Math.exp(-dt / config.input.wheelReleaseTime));
    this.zoomReservoir -= released;
    out.zoomDelta += released;
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    this.pressedKeys.add(event.code);
    if (event.code === "Space") event.preventDefault();
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.pressedKeys.delete(event.code);
  };

  private readonly onBlur = (): void => {
    this.pressedKeys.clear();
    this.orbiting = false;
    this.pressingLeft = false;
    this.pick = false;
  };

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse") return;
    this.updatePointer(event);

    if (event.button === LEFT_BUTTON) {
      this.pressingLeft = true;
      // La désignation vise l'endroit enfoncé, pas l'endroit relâché : en
      // cliquant tout en déplaçant la souris, c'est le premier que le joueur
      // regardait ([TD-044]).
      this.pressPointer.x = this.pointer.x;
      this.pressPointer.y = this.pointer.y;
      return;
    }

    if (event.button === MIDDLE_BUTTON || event.button === RIGHT_BUTTON) {
      this.orbiting = true;
      // Coupe le défilement automatique du clic molette, et la sélection de
      // texte qu'un glissement déclencherait.
      event.preventDefault();
      this.target.setPointerCapture(event.pointerId);
    }
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse") return;
    this.updatePointer(event);
    if (!this.orbiting) return;
    // Vers la droite = tourner vers la droite ; vers le haut = regarder en haut.
    this.lookDelta.x += event.movementX;
    this.lookDelta.y -= event.movementY;
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse") return;
    // Le clic gauche désigne toujours : il n'a pas d'autre fonction, donc rien
    // à départager. Lui imposer un seuil de déplacement perdait les clics faits
    // en déplaçant la souris ([TD-044]).
    if (this.pressingLeft && event.button === LEFT_BUTTON) {
      this.pick = true;
      this.pressingLeft = false;
    } else if (event.button !== LEFT_BUTTON) {
      this.orbiting = false;
    }
    if (this.target.hasPointerCapture(event.pointerId)) {
      this.target.releasePointerCapture(event.pointerId);
    }
  };

  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    // deltaMode 1 = lignes (Firefox) : converti en pixels pour rester homogène.
    const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    this.zoomReservoir -= pixels;
  };

  private readonly onPointerLeave = (event: PointerEvent): void => {
    if (event.pointerType !== "mouse") return;
    this.pointerPresent = false;
  };

  private updatePointer(event: PointerEvent): void {
    if (normalizePointer(event, this.target, this.pointer))
      this.pointerPresent = true;
  }

  private readonly onContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  dispose(): void {
    this.listeners.abort();
    this.pressedKeys.clear();
    this.lookDelta.x = 0;
    this.lookDelta.y = 0;
    this.zoomReservoir = 0;
    this.orbiting = false;
    this.pressingLeft = false;
    this.pointerPresent = false;
    this.pick = false;
  }
}
